/**
 * Common Component Styles
 *
 * Reusable StyleSheet definitions for common UI patterns.
 * Eliminates duplication of frequently used component styles.
 *
 * Usage:
 * - Import: import { CommonStyles } from '../constants';
 * - Apply: <View style={CommonStyles.container}>
 * - Compose: <View style={[CommonStyles.card, styles.customCard]}>
 */

import { StyleSheet, Platform } from 'react-native';
import { Colors } from './colors';
import { Spacing, BorderRadius } from './layout';
import { Shadows } from './shadows';

export const CommonStyles = StyleSheet.create({
  // === CONTAINERS ===

  /**
   * Full-screen container with background
   * Use for screen-level components
   */
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },

  /**
   * Centered container (flex center)
   * Use for loading states, empty states
   */
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  /**
   * Content container with max width for desktop
   * Use inside ScrollView for responsive layouts
   */
  contentContainer: {
    width: '100%',
    padding: Spacing.base,
    ...(Platform.OS === 'web' && {
      maxWidth: 1200,
      backgroundColor: Colors.contentArea,
    }),
  },

  // === CARDS ===

  /**
   * Standard card with shadow and padding
   * Use for content grouping
   */
  card: {
    backgroundColor: Colors.white,
    borderRadius: BorderRadius.xl,
    padding: Spacing.base,
    ...Shadows.sm,
  },

  /**
   * Card section with consistent padding
   * Use inside cards for section grouping
   */
  cardSection: {
    padding: Spacing.base,
  },

  // === DIVIDERS ===

  /**
   * Horizontal divider line
   * Use between sections
   */
  divider: {
    height: 1,
    backgroundColor: Colors.border,
    marginVertical: Spacing.md,
  },

  /**
   * Section divider with more spacing
   * Use between major sections
   */
  sectionDivider: {
    height: 1,
    backgroundColor: Colors.border,
    marginVertical: Spacing.xl,
  },

  // === FAB ===
  // The floating "+" is the `Fab` component (src/components/Fab.tsx) — design
  // v2: 56px, `Radii.fab` (12), `Colors.fabAccent`, above the bottom TabBar.

  // === EMPTY STATES ===

  /**
   * Empty state container with padding
   */
  emptyContainer: {
    padding: Spacing['2xl'],
    alignItems: 'center',
  },

  /**
   * Empty state primary text
   */
  emptyText: {
    fontSize: 18,
    color: Colors.textMedium,
    marginTop: Spacing.base,
    marginBottom: Spacing.sm,
  },

  /**
   * Empty state secondary text (subtitle)
   */
  emptySubtext: {
    fontSize: 14,
    color: Colors.textLight,
    textAlign: 'center',
    marginBottom: Spacing.xl,
  },

  // === SECTION HEADERS ===

  /**
   * Section header container (title + action)
   */
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: Spacing.base,
  },

  /**
   * Section title text
   */
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: Colors.text,
  },

  /**
   * Section title container (icon + text)
   */
  sectionTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },

  // === BUTTONS ===

  /**
   * Primary button for CTAs
   */
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.primary,
    paddingHorizontal: Spacing.xl,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.base,
    gap: Spacing.sm,
  },

  /**
   * Button text
   */
  addButtonText: {
    fontSize: 16,
    color: Colors.white,
    fontWeight: '600',
  },

  /**
   * Link-style text button
   */
  linkButton: {
    fontSize: 16,
    color: Colors.primary,
    fontWeight: '600',
  },
});
